import React from 'react';
import { Loader2 } from 'lucide-react';

const SIZE_CLASSES = {
  sm: 'text-xs px-3 py-1.5 gap-1.5',
  md: 'text-sm px-4 py-2 gap-2',
  lg: 'text-base px-5 py-2.5 gap-2',
};

const VARIANT_CLASSES = {
  light: {
    primary: 'bg-themePrimary text-white shadow-sm hover:brightness-110 hover:shadow-md focus-visible:ring-themePrimary/40',
    secondary: 'bg-white text-slate-700 border border-slate-300 shadow-sm hover:bg-slate-50 focus-visible:ring-slate-400/40',
    outline: 'bg-transparent text-slate-700 border border-slate-300 hover:bg-slate-50 focus-visible:ring-slate-400/40',
    danger: 'bg-red-600 text-white shadow-sm hover:bg-red-700 focus-visible:ring-red-400/40',
    ghost: 'bg-transparent text-slate-600 hover:bg-slate-100 focus-visible:ring-slate-400/40',
  },
  dark: {
    primary: 'bg-violet-600 text-white shadow-sm hover:bg-violet-500 focus-visible:ring-violet-400/40',
    secondary: 'bg-slate-800 text-slate-100 border border-slate-700 hover:bg-slate-700 focus-visible:ring-slate-500/40',
    outline: 'bg-transparent text-slate-200 border border-slate-700 hover:bg-slate-800 focus-visible:ring-slate-500/40',
    danger: 'bg-red-600 text-white shadow-sm hover:bg-red-500 focus-visible:ring-red-400/40',
    ghost: 'bg-transparent text-slate-300 hover:bg-slate-800 focus-visible:ring-slate-500/40',
  },
};

// "Sticker" variant: flat fill, thick ink border, hard offset shadow that
// collapses on press — borrowed from the landing page's brand language for
// pages that intentionally bridge the marketing site into the app (Login).
// Bypasses the soft-pill/scale treatment the other variants share.
const STICKER_BASE =
  'font-bricolage inline-flex items-center justify-center rounded-[10px] font-bold border-2 border-[#241f1a] ' +
  'shadow-[4px_4px_0_0_#241f1a] transition-[transform,box-shadow] duration-150 ' +
  'hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[6px_6px_0_0_#241f1a] ' +
  'active:translate-x-0.5 active:translate-y-0.5 active:shadow-[2px_2px_0_0_#241f1a] ' +
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-x-0 disabled:hover:translate-y-0 disabled:hover:shadow-[4px_4px_0_0_#241f1a]';
const STICKER_FILL = 'bg-[#e0bb49] text-[#241f1a]';

const Button = React.forwardRef(
  (
    {
      variant = 'primary',
      tone = 'light',
      size = 'md',
      loading = false,
      disabled = false,
      fullWidth = false,
      icon: Icon,
      iconPosition = 'left',
      className = '',
      children,
      type = 'button',
      ...rest
    },
    ref
  ) => {
    const isDisabled = disabled || loading;
    const isSticker = variant === 'sticker';
    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        className={[
          isSticker
            ? STICKER_BASE
            : [
                'inline-flex items-center justify-center rounded-lg font-medium transition-all duration-150',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                'active:scale-[0.98]',
                'disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100',
                (VARIANT_CLASSES[tone] || VARIANT_CLASSES.light)[variant],
              ].join(' '),
          isSticker ? STICKER_FILL : '',
          fullWidth ? 'w-full' : '',
          SIZE_CLASSES[size],
          className,
        ].join(' ')}
        {...rest}
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          Icon && iconPosition === 'left' && <Icon className="w-4 h-4 flex-shrink-0" />
        )}
        {children && <span>{children}</span>}
        {!loading && Icon && iconPosition === 'right' && <Icon className="w-4 h-4 flex-shrink-0" />}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
